import { format } from 'date-fns';
import { createServer, Response } from 'miragejs';

import { frames } from 'mocks/server/data/frames';
import { statusHistory } from 'mocks/server/data/statusHistory';
import { AZURE_AD_AUTH_PATHS, END_DATE, START_DATE } from 'mocks/server/utils/constants';
import { Event, generateEvents } from 'mocks/server/utils/events';
import { frameDetails } from 'mocks/server/utils/frameDetails';
import { framesBySearch } from 'mocks/server/utils/framesBySearch';
import { framesByStatus } from 'mocks/server/utils/framesByStatus';
import { framesCsvBody } from 'mocks/server/utils/framesCsvBody';
import { generateFilters } from 'mocks/server/utils/generateFilters';
import { monetisation } from 'mocks/server/utils/monetisation';
import { paged } from 'mocks/server/utils/paged';
import { parseDateQueryParam } from 'mocks/server/utils/parseDateQueryParam';
import { statusAggregate } from 'mocks/server/utils/statusAggregate';
import {
  FILTERS_API_URL,
  FRAMES_API_URL,
  MONETISATION_API_URL,
  STATUS_API_URL,
} from 'utils/environmentVariables';

/**
 * Link to MirageJS -> https://miragejs.com/
 * MirageJS is used to intercept network traffic and dynamically return mocked results
 */
export const makeServer = ({ environment = 'development' } = {}) =>
  createServer({
    environment,
    routes() {
      // Allows the network traffic for Azure AD authentication to pass through
      // More info here -> https://miragejs.com/api/classes/server/#passthrough
      this.passthrough(AZURE_AD_AUTH_PATHS);

      const events = generateEvents(START_DATE, END_DATE);

      this.get(FILTERS_API_URL, (schema, request) => {
        return generateFilters(frames);
      });

      this.get(STATUS_API_URL, (schema, request) => {
        const by = request.queryParams.by;
        return statusAggregate(by as string);
      });

      this.get(MONETISATION_API_URL, (schema, request) => {
        return monetisation();
      });

      this.get(FRAMES_API_URL, (schema, request) => {
        const status = request.queryParams.status;
        const filteredFrames = framesByStatus(status as string, frames);
        const page = Number(request.queryParams.page);
        const pageSize = Number(request.queryParams.pageSize);
        if (page && pageSize) {
          return paged(filteredFrames, page, pageSize);
        }
        return paged(filteredFrames, 1, 10);
      });

      this.get(`${FRAMES_API_URL}/export`, (schema, request) => {
        const status = request.queryParams.status;
        const body = framesCsvBody(status as string, frames);
        const dateTimeStamp = new Date();
        const dateTimeString = format(dateTimeStamp, 'yyyy_MM_dd_HH_mm_ss');
        return new Response(
          200,
          {
            'Content-Type': 'application/csv',
            'X-Filename': `frame_monitoring_${dateTimeString}.csv`,
          },
          body
        );
      });

      this.get(`${FRAMES_API_URL}/search`, (schema, request) => {
        const searchTerm = request.queryParams.searchTerm;
        return paged(framesBySearch(searchTerm as string, frames), 1, 10);
      });

      this.get(`${FRAMES_API_URL}/:id`, (schema, request) => {
        const id = request.params.id;
        return frameDetails(id as string, frames);
      });

      this.get(`${FRAMES_API_URL}/ids`, (schema, request) => {
        return frames.map((frame) => frame.id);
      });

      this.get(`${FRAMES_API_URL}/:id/events`, (schema, request) => {
        const from = request.queryParams.from as string;
        const to = request.queryParams.to as string;
        const fromDateTime = parseDateQueryParam(from);
        const toDateTime = parseDateQueryParam(to);

        return events.filter((event: Event) => {
          return event.time >= fromDateTime.getTime() && event.time <= toDateTime.getTime();
        });
      });

      this.get(`${FRAMES_API_URL}/:id/status`, (schema, request) => {
        return statusHistory;
      });

      this.get(`${FRAMES_API_URL}/:id/monetisation`, (schema, request) => {
        return monetisation();
      });
    },
  });
